import type { NotificationResponseDto } from '@/lib/models';

/** The loosely-typed `meta` bag every notification carries (marketId / amounts / …). */
export type NotificationMeta = NotificationResponseDto['meta'] | undefined;

/** Read a string field off the loosely-typed `meta` payload, if present + non-empty. */
export function metaString(meta: NotificationMeta, key: string): string | undefined {
  const value = meta?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Read a finite number field off `meta`, if present. */
export function metaNumber(meta: NotificationMeta, key: string): number | undefined {
  const value = meta?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Read a boolean field off `meta`, if present. */
export function metaBool(meta: NotificationMeta, key: string): boolean | undefined {
  const value = meta?.[key];
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * The market/event deep-link id used by the "View market/position" navigation —
 * `meta.eventId` preferred, falling back to `meta.marketId` (same field the
 * legacy detail pop-up used). Undefined when neither is set.
 */
export function marketDeepLinkId(meta: NotificationMeta): string | undefined {
  return metaString(meta, 'eventId') ?? metaString(meta, 'marketId');
}

/** The market/event display name — `marketTitle` preferred, then `marketName`. */
export function marketTitle(meta: NotificationMeta): string | undefined {
  return metaString(meta, 'marketTitle') ?? metaString(meta, 'marketName');
}

/**
 * Contract price stored in dollars (0–1) → the cents label players see.
 * Values > 1 are assumed to already be cents. Mirrors the helper in
 * notification-detail-facts so both surfaces render prices identically.
 */
export function priceCentsLabel(price: number): string {
  const cents = price <= 1 ? Math.round(price * 100) : Math.round(price);
  return `${cents}¢`;
}
